'use client';
import { cn } from '@/components/ui/cn';
import { Stack } from '@/components/ui/layout';
import { Text as NativeText } from '@/components/ui/text';
import layoutStyles from './reset-password-page.layout.module.css';

import { Link, linkClasses } from '@/components/ui/link';
import { config } from '@/config';
import { Link as RouterLink } from 'react-router';

import type { PasswordResetTokenResponse } from '../api/password-reset/types';
import { CompletePasswordResetForm } from '../components/complete-password-reset-form';
import { PublicAuthShell } from '../components/public-auth-shell';
import { ResetPasswordForm } from '../components/reset-password-form';

const PASSWORD_RESET_STATE_COPY = {
  already_completed: {
    description:
      'This reset link has already been used. Sign in with your updated password, or request another reset email if you still need access.',
    title: 'This password reset link has already been used.',
  },
  expired: {
    description:
      'Password reset links stay active for 2 hours. Return to forgot password to request another reset email and start again.',
    title: 'This password reset session has expired.',
  },
  invalid: {
    description:
      'The link may be incomplete, replaced by a newer reset request, or no longer active. Request another reset email to continue.',
    title: 'This password reset link is invalid.',
  },
} as const;

type ResetPasswordPageProps = {
  hasToken?: boolean;
  isLoading?: boolean;
  passwordResetToken?: PasswordResetTokenResponse | null;
};

export function ResetPasswordPage({
  hasToken = false,
  isLoading = false,
  passwordResetToken = null,
}: ResetPasswordPageProps) {
  const tokenState = passwordResetToken?.state;
  const showCompletionForm =
    hasToken && tokenState === 'valid' && Boolean(passwordResetToken?.tokenId);

  return (
    <PublicAuthShell title="Reset your Minerva password">
      <>
        {isLoading && hasToken && (
          <Stack className={cn(layoutStyles.layout1)}>
            <NativeText variant="h5" className={cn(layoutStyles.layout2)}>
              Checking your password reset link...
            </NativeText>
          </Stack>
        )}

        {!isLoading && !hasToken && <ResetPasswordForm />}

        {!isLoading && showCompletionForm && passwordResetToken?.tokenId && (
          <CompletePasswordResetForm tokenId={passwordResetToken.tokenId} />
        )}

        {!isLoading &&
          hasToken &&
          !showCompletionForm &&
          tokenState &&
          tokenState !== 'valid' && (
            <Stack className={cn(layoutStyles.layout3)}>
              <NativeText variant="h5" className={cn(layoutStyles.layout4)}>
                {PASSWORD_RESET_STATE_COPY[tokenState].title}
              </NativeText>
              <NativeText className={cn(layoutStyles.layout5)}>
                {PASSWORD_RESET_STATE_COPY[tokenState].description}
              </NativeText>
              <NativeText className={cn(layoutStyles.layout6)}>
                You can{' '}
                <RouterLink
                  className={linkClasses({ className: 'font-semibold' })}
                  to="/reset-password"
                >
                  request another password reset email
                </RouterLink>{' '}
                or contact{' '}
                <Link
                  className="font-semibold"
                  href={`mailto:${config.SUPPORT_EMAIL}`}
                >
                  Minerva support
                </Link>
                .
              </NativeText>
            </Stack>
          )}
      </>
    </PublicAuthShell>
  );
}
